import { useState } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { useAppDispatch } from 'app/store';
import { CropOptions, ImageAttributes } from 'types/general.types';
import { addAlertAction } from 'app/store/Alerts/slice';
import { TEST_ID_LOADING_INDICATOR } from 'app/utils/test/constants';
import {
  Box,
  Button,
  Center,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
} from '@chakra-ui/react';
import { ImageCrop } from 'app/components/Profile/ProfileImage/ImageCrop';
import { MIN_ALLOWED_DIMENSION } from 'app/components/Profile/ProfileImage/constants';
import { CropImageModalProps } from 'app/components/Core/UploadImageModal/types';
import { imageAttributesDefault } from 'app/components/Core/UploadImageModal/constants';
import { useCropOptions } from 'app/components/Core/UploadImageModal/hooks';

export const CropImageModal = ({
  title,
  avatar,
  onClose,
  startPolling,
  effect,
  minAllowedDimension,
}: CropImageModalProps) => {
  const dispatch = useAppDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const [cancelToken, setCancelToken] = useState<CancelTokenSource>();
  const [imageAttributes, setImageAttributes] = useState<ImageAttributes>(
    imageAttributesDefault
  );
  const {
    updateCropOptions,
    calculateCropOptions,
    cropOptions,
  } = useCropOptions(imageAttributes);
  const minDimension = minAllowedDimension || MIN_ALLOWED_DIMENSION;
  const isUploadCancelable = cancelToken && isUploading;

  const handleClose = () => {
    onClose();
    setIsUploading(false);
  };

  const handleSubmit = async () => {
    if (
      imageAttributes.naturalHeight < minDimension ||
      imageAttributes.naturalWidth < minDimension
    ) {
      dispatch(addAlertAction({ message: 'Image is too small' }));
      return;
    }
    if (cropOptions && avatar) {
      setIsUploading(true);
      const cropPayload: CropOptions = calculateCropOptions(
        cropOptions,
        imageAttributes
      );
      try {
        const cancelTokenSource = axios.CancelToken.source();
        setCancelToken(cancelTokenSource);
        const pollingLink = await dispatch(
          effect({
            image: {
              fileName: avatar.fileName,
              base64: avatar.base64,
              file: avatar.file,
              cropOptions: cropPayload,
            },
            cancelTokenSource,
          })
        ).unwrap();

        if (!pollingLink) {
          setIsUploading(false);
        } else {
          setCancelToken(undefined);
        }
        const { success = false } = await startPolling(pollingLink);

        if (success) {
          dispatch(
            addAlertAction({ message: 'Profile image has been uploaded' })
          );
        } else {
          dispatch(addAlertAction({ message: 'Profile image upload failed' }));
        }
        handleClose();
      } catch (e) {
        setCancelToken(undefined);
        setIsUploading(false);
      }
    }
  };

  if (!avatar) {
    return null;
  }

  return (
    <Modal isOpen onClose={handleClose} variant="blurredBg">
      <ModalOverlay />
      <ModalContent
        width={avatar && !isUploading ? '600px' : '560px'}
        maxWidth="600px"
        data-testid="upload-image-dialog"
        aria-labelledby="Image Upload"
        aria-describedby="Update/upload image"
      >
        <ModalHeader title={title}>{title}</ModalHeader>
        <ModalBody sx={isUploading ? {} : { p: 0, mx: 0 }}>
          {isUploading ? (
            <Box width="full" data-testid={TEST_ID_LOADING_INDICATOR}>
              <Text pb={2}>
                {isUploadCancelable ? 'Uploading' : 'Processing'}
              </Text>
              <Progress
                size="sm"
                isIndeterminate
                sx={{
                  '[role="progressbar"]': {
                    bg: 'B.0',
                  },
                }}
              />
            </Box>
          ) : (
            <>
              <Center
                width="100%"
                height="100%"
                minHeight="320px"
                maxHeight="368px"
                data-testid="crop-container"
              >
                <ImageCrop
                  src={avatar.base64}
                  onCropChange={updateCropOptions}
                  setImageAttributes={setImageAttributes}
                />
              </Center>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              if (isUploadCancelable) {
                cancelToken?.cancel();
              }
              handleClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            ml={4}
            onClick={handleSubmit}
            isDisabled={!avatar || isUploading}
            data-testid="image-submit-button"
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
